import { useId, useState, type InputHTMLAttributes } from "react";

import { type ContactDraft } from "./contactDraft";
import {
  normalizeDomain,
  validateDomain,
  validateEmail,
} from "../entities/counterparties";

interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "id"
> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Сообщение показывается сразу под полем и читается скринридером. */
  error?: string | null;
  hint?: string;
  multiline?: boolean;
}

/**
 * Поле формы с подписью, подсказкой и ошибкой под ним. Ошибка привязана к полю
 * через aria-describedby, поэтому её слышит не только тот, кто её видит.
 */
export function TextField({
  label,
  value,
  onChange,
  error = null,
  hint,
  multiline = false,
  ...rest
}: TextFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error === null ? null : errorId, hint === undefined ? null : hintId]
      .filter(Boolean)
      .join(" ") || undefined;
  const common = {
    id,
    value,
    "aria-invalid": error === null ? undefined : true,
    "aria-describedby": describedBy,
  } as const;

  return (
    <div className="field cp-field">
      <label htmlFor={id}>
        <span>{label}</span>
      </label>
      {multiline ? (
        <textarea
          {...common}
          rows={3}
          maxLength={rest.maxLength}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          {...rest}
          {...common}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {hint === undefined ? null : (
        <small id={hintId} className="cp-hint">
          {hint}
        </small>
      )}
      {error === null ? null : (
        <small id={errorId} className="field-error" role="alert">
          {error}
        </small>
      )}
    </div>
  );
}

/**
 * Домены контрагента. Отдельного маршрута для доменов в Core нет: список
 * уходит целиком в теле контрагента, поэтому здесь он редактируется как одно
 * значение и сохраняется вместе с остальными реквизитами.
 */
export function DomainListField({
  domains,
  onChange,
  error = null,
}: {
  domains: readonly string[];
  onChange: (domains: string[]) => void;
  error?: string | null;
}) {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);
  const inputId = useId();
  const errorId = `${inputId}-error`;

  function add() {
    // Из буфера часто приходит сразу несколько доменов.
    const parts = draft
      .split(/[\s,;]+/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      setDraftError("Укажите домен");
      return;
    }
    for (const part of parts) {
      const problem = validateDomain(part);
      if (problem !== null) {
        setDraftError(`«${part}»: ${problem}`);
        return;
      }
    }
    const next = [...domains];
    for (const part of parts) {
      const domain = normalizeDomain(part);
      if (!next.includes(domain)) next.push(domain);
    }
    onChange(next);
    setDraft("");
    setDraftError(null);
  }

  const shownError = draftError ?? error;

  return (
    <div className="field cp-field">
      <label htmlFor={inputId}>
        <span>Email-домены</span>
      </label>
      <small className="cp-hint">
        Письма с этих доменов относятся к контрагенту, если нет более сильного
        основания.
      </small>
      {domains.length === 0 ? null : (
        <ul className="cp-chips" aria-label="Домены контрагента">
          {domains.map((domain) => (
            <li key={domain} className="cp-chip">
              <span>{domain}</span>
              <button
                type="button"
                className="cp-chip__remove"
                aria-label={`Удалить домен ${domain}`}
                onClick={() => onChange(domains.filter((d) => d !== domain))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="cp-inline-add">
        <input
          id={inputId}
          value={draft}
          placeholder="example.ru"
          autoComplete="off"
          aria-invalid={shownError === null ? undefined : true}
          aria-describedby={shownError === null ? undefined : errorId}
          onChange={(event) => {
            setDraft(event.target.value);
            setDraftError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="button button--secondary button--compact"
          onClick={add}
        >
          Добавить домен
        </button>
      </div>
      {shownError === null ? null : (
        <small id={errorId} className="field-error" role="alert">
          {shownError}
        </small>
      )}
    </div>
  );
}

/**
 * Поля контакта: email обязателен и проверяется рядом с полем — при уходе из
 * поля и при отправке.
 */
export function ContactFields({
  draft,
  onChange,
  showErrors,
  serverErrors = {},
}: {
  draft: ContactDraft;
  onChange: (draft: ContactDraft) => void;
  showErrors: boolean;
  serverErrors?: Record<string, string>;
}) {
  const [emailTouched, setEmailTouched] = useState(false);
  const emailError =
    showErrors || emailTouched
      ? (validateEmail(draft.email) ?? serverErrors.email ?? null)
      : (serverErrors.email ?? null);

  return (
    <div className="cp-contact-fields">
      <TextField
        label="Email контакта"
        type="email"
        autoComplete="off"
        maxLength={320}
        value={draft.email}
        error={emailError}
        onBlur={() => setEmailTouched(true)}
        onChange={(email) => onChange({ ...draft, email })}
      />
      <TextField
        label="Имя"
        maxLength={255}
        value={draft.name}
        error={serverErrors.name ?? null}
        onChange={(name) => onChange({ ...draft, name })}
      />
      <TextField
        label="Должность"
        maxLength={255}
        value={draft.position}
        error={serverErrors.position ?? null}
        onChange={(position) => onChange({ ...draft, position })}
      />
      <label className="cp-check">
        <input
          type="checkbox"
          checked={draft.is_primary}
          onChange={(event) =>
            onChange({ ...draft, is_primary: event.target.checked })
          }
        />
        <span>Основной контакт</span>
      </label>
    </div>
  );
}
